<script setup lang="ts">
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import {ref, watch} from "vue";
import {CalendarOffer} from "@/models/SavedOffer";
import {getCalendarOffers} from "@/api";
import {Boat} from "@/models/Boat";
import CustomButton from "@/components/Button/Button.vue";
import { useI18n } from 'vue-i18n';
import {useUserStore} from "@/stores/userStore";

const { t } = useI18n();
const userStore = useUserStore();

const props = defineProps({
  id: String,
  show: Boolean,
})

defineEmits<{
  'on-close': [void]
}>();

const offers = ref<{offers: CalendarOffer[], boats: Boat[]}>({offers: [], boats: []});
const euro = new Intl.NumberFormat('el-GR', {
  style: 'currency',
  currency: 'EUR',
});
const selectedCalendarOffer = ref<{ yacht: string, offer: string } | null>(null)

watch(() => props.show, (show) => {
  if (!show) return;

  getCalendarOffers(props.id)
      .then(data => {
        offers.value = data.data;
      })
      .catch(e => {
        console.error(e)
      })
})
</script>

<template>
  <CustomModal :show="show">
    <div class="w-[1338px] h-[756px]">
      <div class="sticky-header p-4 bg-gradient-to-l from-gradients-ghostWhite to-gradients-aliceBlue flex flex-col">
        <div class="cursor-pointer self-end" type="default" @click="$emit('on-close')">
          <img src="@/assets/icons/svg/Close_16x16.svg" :alt="t('close')" width="18" />
        </div>
        <div class="flex items-center justify-between mt-6">
          <div class="responsive-title-5 mt-15 pb-12 pl-14 font-semibold">{{ t('priceCalendar') }}</div>
          <div class="mr-16 flex flex-row items-center gap-3.5 mt-4">
            <div>
              <img src="@/assets/icons/svg/Left_16x16.svg" :alt="t('leftArrow')" />
            </div>
            <div v-for="offer in offers.offers" :key="offer.uuid" class="cursor-pointer">
              <div
                :class="`responsive-title-5 font-semibold border-b-2 px-12 ${selectedCalendarOffer?.offer === offer.uuid ? 'border-primary-blue' : 'border-b-black'}`">
                {{ userStore.formatDate(new Date(offer.date)) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-row px-14 pt-5.5">
        <div class="flex flex-col gap-8">
          <div v-for="boat in offers.boats" :key="boat.uuid" class="flex flex-row items-center gap-4">
            <div
              :class="`flex flex-row items-center gap-4 ${selectedCalendarOffer?.yacht === boat.uuid ? 'border-2 border-primary-blue rounded-11xl' : ''} pr-5`">
              <img :src="boat.image" :alt="t('boatImage')" class="w-24 rounded-9xl" />
              <div class="py-2">
                <div class=" responsive-body-medium font-semibold">{{ boat.name }}</div>
                <div class="flex flex-row items-center gap-3 mt-1.5">
                  <div class="flex flex-row items-center gap-2.5">
                    <img src="@/assets/icons/svg/Bed_16x16.svg" :alt="t('beds')" />
                    <span class="responsive-body-xxsmall">{{ boat.specs.beds }} {{ t('beds') }}</span>
                  </div>
                  <div class="flex flex-row items-center gap-2.5">
                    <img src="@/assets/icons/svg/Cabin_16x16.svg" :alt="t('cabins')" />
                    <span class="responsive-body-xxsmall">{{ boat.specs.cabins }} {{ t('cabins') }}</span>
                  </div>
                  <div class="flex flex-row items-center gap-2.5">
                    <img src="@/assets/icons/svg/WC_16x16.svg" :alt="t('wc')" />
                    <span class="responsive-body-xxsmall">{{ boat.specs.wc }} WC</span>
                  </div>
                </div>
              </div>
            </div>
            <button @click="offers = {...offers, boats: offers.boats.filter(boat1 => boat1.uuid !== boat.uuid)}">
              <img src="@/assets/icons/svg/Delete_16x16.svg" :alt="t('delete')" />
            </button>
          </div>
        </div>
        <div class="flex flex-col gap-8 flex-grow-1 items-center ml-36">
          <div v-for="yacht in offers.boats" :key="yacht.uuid" class="flex flex-row items-center gap-3.5 h-25">
            <div v-for="offer in offers.offers" :key="`offer-${offer.uuid}`">
              <div v-if="offer.boats.find(yacht1 => yacht1 === yacht.uuid)">
                <div
                  :class="` responsive-body-medium font-semibold h-14 w-56 flex items-center justify-center ${(selectedCalendarOffer?.offer === offer.uuid && selectedCalendarOffer?.yacht === yacht.uuid) ? 'border border-primary-blue' : 'border border-black/10'} rounded-11xl cursor-pointer `"
                  @click="selectedCalendarOffer = {yacht: yacht.uuid, offer: offer.uuid}">
                  € {{ euro.format(offers.boats.find(yacht1 => yacht1.uuid === yacht.uuid).pricing.newPrice).slice(0,
                  -1) }}
                </div>
              </div>
              <div v-else class="h-14 w-56 flex items-center justify-center border border-black/10 rounded-11xl">
                <div>{{ t('noAvailability') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="sticky-footer w-full h-24 bg-gradient-to-l from-gradients-ghostWhite to-gradients-aliceBlue px-16 py-7 flex flex-row items-center justify-between">
        <div>
          <div class=" responsive-body-small font-bold">{{ offers.boats.find(offer => offer.uuid ===
    selectedCalendarOffer?.yacht)?.name }}</div>
          <div>{{ userStore.formatDate(new Date(offers.offers.find(offer => offer.uuid ===
            selectedCalendarOffer?.offer)?.date)) }}</div> <!-- Format the selected offer's date -->
        </div>
 
        <div v-if="offers.boats.find(offer => offer.uuid === selectedCalendarOffer?.yacht)?.pricing.newPrice"
          class="flex flex-row items-center gap-6">
          <div class="responsive-title-5 font-bold">€ {{ euro.format(offers.boats.find(offer => offer.uuid ===
            selectedCalendarOffer?.yacht).pricing.newPrice).slice(0, -1) }}</div>
          <CustomButton type="black" :rounded="true" class="h-14" size="large">{{ t('request') }}</CustomButton>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<style scoped>
.sticky-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    width: 100%;
}
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 10;
}
</style>
